import React, { useEffect, useState } from 'react';
import { Spin, Alert, Typography, Card } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { authManager } from '../../auth/AuthManager';

const { Title, Text } = Typography;

export const AuthCallback: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  
  // Check for dark mode preference
  const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

  useEffect(() => {
    const handleCallback = async () => {
      try {
        await authManager.handleCallback();
        setStatus('success');

        // If this is a popup window, close it after showing success briefly
        if (window.opener) {
          setTimeout(() => {
            window.close();
          }, 1500);
        } else {
          // If not a popup, redirect to main app
          setTimeout(() => {
            window.location.href = '/';
          }, 2000);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
        setError(errorMessage);
        setStatus('error');

        // If this is a popup, notify parent and close
        if (window.opener) {
          window.opener.postMessage({ 
            type: 'KEYCLOAK_AUTH_ERROR', 
            error: errorMessage 
          }, window.location.origin);

          setTimeout(() => {
            window.close();
          }, 3000);
        }
      }
    };

    // Small delay to ensure component is mounted
    const timer = setTimeout(handleCallback, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" style={{ color: '#646cff' }} />
            <Title level={4} style={{ 
              marginTop: 16, 
              marginBottom: 8,
              color: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : undefined
            }}>
              Authenticating...
            </Title>
            <Text type="secondary" style={{
              color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : undefined
            }}>
              Please wait while we complete your sign-in process.
            </Text>
          </div>
        );

      case 'success':
        return (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
            <Title level={4} style={{ marginBottom: 8, color: '#52c41a' }}>
              Authentication Successful!
            </Title>
            <Text type="secondary" style={{
              color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : undefined
            }}>
              {window.opener 
                ? 'You can close this window now.'
                : 'Redirecting you to the application...'}
            </Text>
          </div>
        );

      case 'error':
        return (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <ExclamationCircleOutlined style={{ fontSize: 48, color: '#ff4d4f', marginBottom: 16 }} />
            <Title level={4} style={{ marginBottom: 16, color: '#ff4d4f' }}>
              Authentication Failed
            </Title>
            <Alert
              message="Sign-in Error"
              description={error || 'An unexpected error occurred during authentication.'}
              type="error"
              showIcon
              style={{ textAlign: 'left', marginBottom: 16 }}
            />
            <Text type="secondary" style={{
              color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : undefined
            }}>
              {window.opener 
                ? 'This window will close automatically.'
                : 'Please try signing in again.'}
            </Text>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: isDarkMode ? '#242424' : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      padding: '20px'
    }}>
      <Card 
        style={{
          maxWidth: 400,
          width: '100%',
          background: isDarkMode ? 'rgba(40, 40, 40, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: 16,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          border: 'none',
          color: isDarkMode ? '#ffffff' : undefined
        }}
        variant='outlined'
      >
        {renderContent()}
      </Card>
    </div>
  );
}; 